export type MetaPageInput = {
    page_id: string;
    countries: string[];
    is_active: boolean;
}

export type MetaPageRecord = {
    page_id: string;
    page_name: string | null;
    countries: string[];
    is_active: boolean;
    last_fetch_date: string | null;
    last_ads_count: number | null;
}

export type MetaPageValidationResult = {
    exists: boolean;
    pageName: string | null;
    category: string | null;
    activeAdsCount: number | null;
}

export type LinkedinTermInput = {
    search_term: string;
    countries: string[];
    is_active: boolean;
}

export type LinkedinTermRecord = {
    search_term: string;
    countries: string[];
    is_active: boolean;
    last_fetch_date: string | null;
    last_ads_count: number | null;
}

export type Brand = {
    brandKey: string;
    name: string;
    keywords?: string[];
    advertiserDomains: string[];
    competingBrands: BrandSummary[];
    logoPath?: string;
    // Written back via the payload's `aiPresenceTopics`; the elaborate list response returns the saved
    // values under `aiTopics`, which the brand drawer reads to prefill in edit mode.
    aiPresenceTopics?: string[];
    aiTopics?: string[];
    metaPages?: MetaPageInput[];
    // Free-text match terms (DEV-583): substrings matched against normalized click URLs / creative
    // OCR text. Client always sends the full current list (empty array = clear); server normalizes.
    clickUrlTerms?: string[];
    ocrTerms?: string[];
}

export type BrandPayload = Omit<Brand, 'competingBrands'> & {
    competingBrands: string[];
    aiPresenceTopics?: string[];
    linkedinTerms?: LinkedinTermInput[];
};

export type BrandsResponse = {
    brands: Brand[] | BrandSummary[];
    total: number;
}

export type BrandSummary = {
    name: string;
    brandKey: string;
}

export type ManagementRequest = {
    offset?: number;
    limit?: number;
    elaborate?: boolean;
    term?: string;
    sortBy?: string;
    sortDir?: 'asc' | 'desc';
}

export type SortState = {
    sortBy: string;
    sortDir: 'asc' | 'desc';
}

export enum BrandManagementColumn {
    BRAND_KEY = 'brandKey',
    BRAND_NAME = 'name',
    KEYWORDS = 'keywords',
    ADVERTISER_DOMAINS = 'advertiserDomains',
    COMPETITORS = 'competingBrands',
    ACCOUNTS = 'accountNames',
    CREATED_AT = 'createdAt',
}

export type UsersBrands = {
    key: string;
    name: string;
}

export type UserBase = {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    verifiedEmail?: boolean;
};

export type User = UserBase & {
    brands: UsersBrands[];
    account: { key: string };
};

export type UserPayload = UserBase & {
    accountKey: string;
};

export type UserResponse = {
    users: User[],
    total: number
}

export enum UserManagementColumn {
    EMAIL = 'email',
    FIRST_NAME = 'firstName',
    LAST_NAME = 'lastName',
    ROLE = 'role',
    CONNECTED_BRANDS = 'brands',
    ACCOUNT = 'account',
    CREATED_AT = 'createdAt',
    LAST_LOGIN = 'lastLogin',
}

export type BrandLogoResponse = {
    logoURL: string;
}

export type Account = {
    name: string;
    accountKey: string;
    brands: string[];
    userIds: string[];
    createdAt: string;
}

export type AccountUser = {
    email: string;
    firstName: string;
    lastName: string;
}

export type AccountUsersResponse = {
    accountUsers: AccountUser[];
}

export enum AccountManagementColumn {
    NAME = 'name',
    BRANDS = 'brands',
    USERS = 'userIds',
    CREATED_AT = 'createdAt',
    ACCOUNT_KEY = 'accountKey'
}

export type AccountPayload = {
    accountKey: string;
    name: string;
    brandKeys: string[];
}

export type AccountSummary = {
    name: string;
    accountKey: string;
}

export type AccountsResponse = {
    brands: Account[] | AccountSummary[];
    total: number;
}

export type Topic = {
    id: string;
    name: string;
    brandKeys: string[];
}

export type AiTopicsResponse = {
  topics: Topic[];
  total: number;
}

export type DomainConflictBrand = {
  brandKey: string;
  name: string;
}

export type DomainConflict = {
  domain: string;
  brands: DomainConflictBrand[];
}

export type DomainConflictsResponse = {
  conflicts: DomainConflict[];
  hasConflicts: boolean;
}

export type CheckDomainConflictsPayload = {
  domains: string[];
  brandKey?: string;
}

export type ResearchRunStatus = 'PENDING' | 'RUNNING' | 'SUCCEEDED' | 'FAILED';

// One brand-level research request/run (SOCIAL_ADS.BRAND_RESEARCH_RUNS row).
export type ResearchRun = {
  runId: string;
  brandKey: string;
  validFrom: string; // YYYY-MM-DD — estimation covers dates after this
  status: ResearchRunStatus;
  source: 'ON_DEMAND' | 'SCHEDULED';
  requestedBy: string | null;
  error: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
}

// Latest research snapshot per (channel, country) — channel-aware so LinkedIn
// (and future channels) appear here with no client changes.
export type ResearchSnapshot = {
  channel: string;
  country: string;
  validFrom: string;
  researchedAt: string | null;
}

export type BrandResearchStatus = {
  snapshots: ResearchSnapshot[];
  latestRun: ResearchRun | null;
}

export type ResearchMemory = {
  country: string;
  content: string;
  lastModified: string | null;
}

